/**
 * TV Session API Client (TV device pairing — the phone/web side of the
 * TV login handshake at /tv).
 *
 * API Endpoints:
 * - POST   /tv/session/{code}/confirm → confirm a pending TV login (auth required)
 * - DELETE /tv/session/{code}         → cancel a pending TV session
 *
 * The TV app creates the session (POST /tv/session) and long-polls its
 * status; confirming here logs the TV in within one poll cycle (≤25s).
 */

import { isAxiosError } from 'axios';
import { apiClient } from './base-client';

export type TvConfirmErrorReason =
  'not_found' | 'expired' | 'already_confirmed' | 'subscription_required';

export type TvConfirmResult = { ok: true } | { ok: false; reason: TvConfirmErrorReason };

export const tvClient = {
  /**
   * Confirm a TV session code. 404/410/already-confirmed/403 are contract
   * statuses (typo'd code, timed-out code, double confirm, no subscription)
   * — returned as a discriminated result, not thrown. Real failures
   * (5xx, network) propagate to the caller.
   */
  confirm: async (code: string): Promise<TvConfirmResult> => {
    try {
      await apiClient.post(`/tv/session/${encodeURIComponent(code)}/confirm`);
      return { ok: true };
    } catch (error) {
      if (!isAxiosError(error)) throw error;

      const status = error.response?.status;
      const body = error.response?.data as { code?: string } | undefined;

      if (body?.code === 'TV_SESSION_ALREADY_CONFIRMED') {
        return { ok: false, reason: 'already_confirmed' };
      }
      if (status === 404) return { ok: false, reason: 'not_found' };
      if (status === 410) return { ok: false, reason: 'expired' };
      // The base-client interceptor usually redirects subscription 403s to
      // /choose-plan before this returns — kept as a defensive fallback.
      if (status === 403) return { ok: false, reason: 'subscription_required' };

      throw error;
    }
  },

  /**
   * Cancel a pending TV session. 404 is structurally normal — the session
   * already expired or was cancelled on the TV.
   */
  cancel: async (code: string): Promise<void> => {
    try {
      await apiClient.delete(`/tv/session/${encodeURIComponent(code)}`);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) return;
      throw error;
    }
  },
};
